import React, { PropsWithChildren, useEffect, useState } from 'react';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { TenancyContextProvider } from '@/contexts/TenancyContextProvider';
import { UsersListProvider } from '@/contexts/UsersListProvider';
import ErrorBoundary from '@/components/ui/ErrorBoundary';
import { AuthProvider } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Tenancy, User } from '@/contexts/TenancyContext';
// Import functions directly from the context file where they're defined
import { loadCachedUsers, saveCachedUsers } from '@/contexts/TenancyContextProvider';

/**
 * Utility functions to get cached tenancy data
 * These would ideally be imported from a shared location, but for now we're duplicating them
 */
function getTenanciesFromCache(): Tenancy[] {
  try {
    const data = localStorage.getItem('tenancy_cache_v1');
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

function getActiveTenancyFromCache(): Tenancy | undefined {
  try {
    const data = localStorage.getItem('active_tenancy_cache_v1');
    return data ? JSON.parse(data) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * AuditorContextProvider
 * 
 * Provides all necessary contexts for auditor views without the UI elements.
 * Use this when you need the context providers but not the duplicate headers/layout.
 */
export function AuditorContextProvider({ children }: PropsWithChildren<{}>) {
  // Get cached tenancy data
  const [tenancies, setTenancies] = useState<Tenancy[]>(getTenanciesFromCache());
  const [activeTenancy, setActiveTenancy] = useState<Tenancy | undefined>(getActiveTenancyFromCache());
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [enabledModules, setEnabledModules] = useState<string[]>([]);
  
  // Load tenancy data on mount
  useEffect(() => {
    // Make sure window.companyID is set properly
    if (activeTenancy && activeTenancy.id) {
      window.companyID = activeTenancy.id;
      window.companyName = activeTenancy.name;
      window.legalName = activeTenancy.legalName;
      window.activeTenancy = activeTenancy;
      
      // Also load cached users for this tenancy
      if (activeTenancy.id) {
        const cachedUsers = loadCachedUsers(activeTenancy.id);
        setUsers(cachedUsers || []);
      }
      
      // Set loading to false once all data is loaded
      setLoading(false);
      setUsersLoading(false);
    }
  }, [activeTenancy]);
  
  // For auditor view, we need to implement module checking function
  const isModuleEnabled = (moduleName: string) => {
    // For auditor view, assume all modules are enabled
    return true;
  };

  return (
    <ErrorBoundary>
      <UsersListProvider>
        <ThemeProvider>
          <AuthProvider>
            <TenancyContextProvider>
             {/* TenancyContextProvider manages its own state and initialization */}
              {children}
              {/* Toast notifications */}
              <Toaster theme="light" richColors position="bottom-left" closeButton />
            </TenancyContextProvider>
          </AuthProvider>
        </ThemeProvider>
      </UsersListProvider>
    </ErrorBoundary>
  );
}

export default AuditorContextProvider;
